import {useMemo, useState} from 'react';
import {toast} from 'react-toastify';

import {UtilaWebhookOutcomeStatus} from '@afriex/core/domain/enums';

import {simulateProcessorWebhook} from '@/api/seeds';
import {Button, Select, TextArea} from '@/components';
import {getErrorMessage} from '@/utils/errorMessage';

import type {ProcessorWebhookConfig} from './processors';
import {PROCESSOR_WEBHOOKS} from './processors';
import WebhookPrimer from './WebhookPrimer';

const formatSample = (config: ProcessorWebhookConfig) =>
  JSON.stringify(config.buildSampleBody(), null, 2);

export default function WebhookSimulatorForm() {
  const [processorId, setProcessorId] = useState<string>(
    PROCESSOR_WEBHOOKS[0].id,
  );
  const [payload, setPayload] = useState<string>(
    formatSample(PROCESSOR_WEBHOOKS[0]),
  );
  const [loading, setLoading] = useState<boolean>(false);

  const processorOptions = useMemo(
    () =>
      PROCESSOR_WEBHOOKS.map((p) => ({
        key: p.label,
        value: p.id,
      })),
    [],
  );

  const currentProcessor = useMemo(
    () =>
      PROCESSOR_WEBHOOKS.find((p) => p.id === processorId) ??
      PROCESSOR_WEBHOOKS[0],
    [processorId],
  );

  const handleProcessorChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const nextId = event.target.value;
    const next = PROCESSOR_WEBHOOKS.find((p) => p.id === nextId);
    if (!next) {
      return;
    }
    setProcessorId(next.id);
    setPayload(formatSample(next));
  };

  const handleResetSample = () => {
    setPayload(formatSample(currentProcessor));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    let parsed: unknown;
    try {
      parsed = JSON.parse(payload);
    } catch {
      toast.error('Invalid JSON');
      return;
    }

    try {
      setLoading(true);
      const {success, outcome} = await simulateProcessorWebhook(
        processorId,
        parsed,
      );
      if (success && outcome?.status === UtilaWebhookOutcomeStatus.Credited) {
        toast.success(
          `Credited deposit (transactionId: ${outcome.transactionId})`,
        );
      } else if (outcome?.status === UtilaWebhookOutcomeStatus.Skipped) {
        toast.warn(`Webhook processed but skipped: ${outcome.reason}`);
      } else {
        toast.success('Webhook sent');
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to send webhook'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex justify-center mt-6 px-6">
      <div className="flex flex-col w-full md:w-9/12">
        <form
          onSubmit={handleSubmit}
          className="bg-white dark:bg-transparent border-2 border-gray-200 dark:border-dark-borderMid rounded-md p-6"
        >
          <div className="flex flex-col gap-6">
            <Select
              label="Processor"
              name="processor"
              value={processorId}
              onChange={handleProcessorChange}
              options={processorOptions}
              placeholder="Select a processor"
            />

            <WebhookPrimer processor={currentProcessor} />

            <div>
              <TextArea
                label="Webhook Payload (JSON)"
                name="payload"
                value={payload}
                onChange={(e) => setPayload(e.target.value)}
                aria-rowspan={16}
                className="w-full isFull font-mono"
                placeholder="Enter webhook JSON payload..."
                spellCheck={false}
              />
              <button
                type="button"
                onClick={handleResetSample}
                className="mt-2 text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                Reset to sample
              </button>
            </div>

            <Button
              type="submit"
              colorScheme="cyan"
              variant="solid"
              className="h-10"
              isLoading={loading}
              loadingText="Sending..."
              disabled={loading}
            >
              Send Webhook
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
